function _classPrivateFieldLooseBase(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
var id = 0;
function _classPrivateFieldLooseKey(e) { return "__private_" + id++ + "_" + e; }
import { Component } from '@theme/component';
import { center, closest, clamp, mediaQueryLarge, prefersReducedMotion, preventDefault, viewTransition, scheduler } from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

/**
 * Shared viewport observer manager for lazy scroll enablement.
 *
 * Limit the number of compositor layers created by slideshows by only enabling scrolling when the slideshow is in the viewport.
 * Resolves known issues with iOS Safari where too many composition layers will crash the page.
 * When a slideshow is NOT in the viewport, it has overflow: hidden (no compositor layer).
 * When a slideshow enters the viewport, the [in-viewport] attribute is added, enabling scrolling.
 */
var _instance = _classPrivateFieldLooseKey("instance");
var _observer = _classPrivateFieldLooseKey("observer");
class SlideshowViewportObserver {
  constructor() {
    /** @type {IntersectionObserver | null} */
    Object.defineProperty(this, _observer, {
      writable: true,
      value: null
    });
  }
  /**
   * Gets the singleton instance
   * @returns {SlideshowViewportObserver}
   */
  static getInstance() {
    if (!_classPrivateFieldLooseBase(this, _instance)[_instance]) {
      _classPrivateFieldLooseBase(this, _instance)[_instance] = new SlideshowViewportObserver();
    }
    return _classPrivateFieldLooseBase(this, _instance)[_instance];
  }

  /**
   * Registers a slideshow to be observed for viewport visibility
   * @param {Slideshow} slideshow - The slideshow to observe
   */
  observe(slideshow) {
    if (!_classPrivateFieldLooseBase(this, _observer)[_observer]) {
      _classPrivateFieldLooseBase(this, _observer)[_observer] = new IntersectionObserver(entries => {
        for (const entry of entries) {
          const slideshowElement = /** @type {Slideshow} */entry.target;
          if (entry.isIntersecting) {
            slideshowElement.setAttribute('in-viewport', '');
          } else {
            slideshowElement.removeAttribute('in-viewport');
          }
        }
      }, {
        rootMargin: '100px'
      });
    }
    _classPrivateFieldLooseBase(this, _observer)[_observer].observe(slideshow);
  }

  /**
   * Unregisters a slideshow from viewport observation
   * @param {Slideshow} slideshow - The slideshow to unobserve
   */
  unobserve(slideshow) {
    _classPrivateFieldLooseBase(this, _observer)[_observer]?.unobserve(slideshow);
    slideshow.removeAttribute('in-viewport');
  }
}

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
/** @type {SlideshowViewportObserver | null} */
Object.defineProperty(SlideshowViewportObserver, _instance, {
  writable: true,
  value: null
});
var _current = _classPrivateFieldLooseKey("current");
var _disabled = _classPrivateFieldLooseKey("disabled");
var _interval = _classPrivateFieldLooseKey("interval");
var _scroll = _classPrivateFieldLooseKey("scroll");
var _resizeObserver = _classPrivateFieldLooseKey("resizeObserver");
var _intersectionObserver = _classPrivateFieldLooseKey("intersectionObserver");
var _visibleSlides = _classPrivateFieldLooseKey("visibleSlides");
var _setupSlideshowWithoutControls = _classPrivateFieldLooseKey("setupSlideshowWithoutControls");
var _setupSlideshow = _classPrivateFieldLooseKey("setupSlideshow");
var _handleScroll = _classPrivateFieldLooseKey("handleScroll");
var _onTransitionInit = _classPrivateFieldLooseKey("onTransitionInit");
var _onTransitionEnd = _classPrivateFieldLooseKey("onTransitionEnd");
var _sync = _classPrivateFieldLooseKey("sync");
var _dragging = _classPrivateFieldLooseKey("dragging");
var _handleMouseDown = _classPrivateFieldLooseKey("handleMouseDown");
var _handlePointerEnter = _classPrivateFieldLooseKey("handlePointerEnter");
var _handleVisibilityChange = _classPrivateFieldLooseKey("handleVisibilityChange");
var _updateControlsVisibility = _classPrivateFieldLooseKey("updateControlsVisibility");
var _setupIntersectionObserver = _classPrivateFieldLooseKey("setupIntersectionObserver");
var _centerSelectedThumbnail = _classPrivateFieldLooseKey("centerSelectedThumbnail");
var _updateVisibleSlides = _classPrivateFieldLooseKey("updateVisibleSlides");
export class Slideshow extends Component {
  constructor(...args) {
    super(...args);
    Object.defineProperty(this, _updateVisibleSlides, {
      value: _updateVisibleSlides2
    });
    /**
     * Centers the selected thumbnail in the thumbnails container
     * @param {number} index - The index of the selected thumbnail
     * @param {ScrollBehavior} [behavior] - The scroll behavior.
     */
    Object.defineProperty(this, _centerSelectedThumbnail, {
      value: _centerSelectedThumbnail2
    });
    /**
     * Setup IntersectionObserver for efficient visibility tracking of slides
     */
    Object.defineProperty(this, _setupIntersectionObserver, {
      value: _setupIntersectionObserver2
    });
    Object.defineProperty(this, _updateControlsVisibility, {
      value: _updateControlsVisibility2
    });
    /**
     * Setup the slideshow with controls for when there are multiple slides
     */
    Object.defineProperty(this, _setupSlideshow, {
      value: _setupSlideshow2
    });
    /**
     * Setup the slideshow without controls for zero or one slides
     */
    Object.defineProperty(this, _setupSlideshowWithoutControls, {
      value: _setupSlideshowWithoutControls2
    });
    this.requiredRefs = ['scroller'];
    /**
     * The current slide index.
     * @type {number}
     */
    Object.defineProperty(this, _current, {
      writable: true,
      value: 0
    });
    /**
     * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
     * @type {boolean}
     */
    Object.defineProperty(this, _disabled, {
      writable: true,
      value: false
    });
    /**
     * The interval ID for automatic playback.
     * @type {number|undefined}
     */
    Object.defineProperty(this, _interval, {
      writable: true,
      value: undefined
    });
    /**
     * The Scroller instance that manages scrolling.
     * @type {Scroller}
     */
    Object.defineProperty(this, _scroll, {
      writable: true,
      value: void 0
    });
    /**
     * The ResizeObserver instance for monitoring scroller size changes
     * @type {ResizeObserver}
     */
    Object.defineProperty(this, _resizeObserver, {
      writable: true,
      value: void 0
    });
    /**
     * IntersectionObserver for efficient visibility tracking of slides
     * @type {IntersectionObserver | null}
     */
    Object.defineProperty(this, _intersectionObserver, {
      writable: true,
      value: null
    });
    /**
     * Cached visible slides result from IntersectionObserver
     * @type {HTMLElement[]}
     */
    Object.defineProperty(this, _visibleSlides, {
      writable: true,
      value: []
    });
    /**
     * Callback invoked on user initiated scroll to sync the current slide index
     * and emit a slide change event if the index has changed.
     */
    Object.defineProperty(this, _handleScroll, {
      writable: true,
      value: () => {
        const previousIndex = _classPrivateFieldLooseBase(this, _current)[_current];
        const index = _classPrivateFieldLooseBase(this, _sync)[_sync]();
        if (index === previousIndex) return;
        const slide = this.slides?.[index];
        if (!slide) return;
        this.dispatchEvent(new SlideshowSelectEvent({
          index,
          previousIndex,
          userInitiated: true,
          trigger: 'scroll',
          slide,
          id: slide.getAttribute('slide-id')
        }));
      }
    });
    Object.defineProperty(this, _onTransitionInit, {
      writable: true,
      value: () => {
        this.setAttribute('transitioning', '');
      }
    });
    Object.defineProperty(this, _onTransitionEnd, {
      writable: true,
      value: () => {
        _classPrivateFieldLooseBase(this, _updateVisibleSlides)[_updateVisibleSlides]();
        this.removeAttribute('transitioning');
      }
    });
    /**
     * Synchronizes the scroll position and updates the current slide index.
     * @returns {number} The index of the current slide.
     */
    Object.defineProperty(this, _sync, {
      writable: true,
      value: () => {
        const {
          slides
        } = this;
        if (!slides) return this.current = 0;
        if (!_classPrivateFieldLooseBase(this, _scroll)[_scroll]) return this.current = 0;
        const visibleSlides = this.visibleSlides;
        if (!visibleSlides.length) return this.current;
        const {
          axis
        } = _classPrivateFieldLooseBase(this, _scroll)[_scroll];
        const {
          scroller
        } = this.refs;
        const centers = visibleSlides.map(slide => center(slide, axis));
        const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
        const closestCenter = closest(centers, referencePoint);
        const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];
        if (!closestVisibleSlide) return this.current = 0;
        const index = slides.indexOf(closestVisibleSlide);
        return this.current = index;
      }
    });
    Object.defineProperty(this, _dragging, {
      writable: true,
      value: false
    });
    /**
     * Handles the 'mousedown' event to start dragging slides.
     * @param {MouseEvent} event - The mousedown event.
     */
    Object.defineProperty(this, _handleMouseDown, {
      writable: true,
      value: event => {
        const {
          slides
        } = this;
        if (!slides || slides.length <= 1) return;
        if (!(event.target instanceof Element)) return;
        if (this.disabled || _classPrivateFieldLooseBase(this, _dragging)[_dragging]) return;

        // Link/button tıklamalarına müdahale etme – ilk tıklamada çalışsın (çift tıklama bug fix)
        if (event.target.closest('a, button, [role="button"]')) return;

        // Check if the event target is within a 3D model interactive element
        // This prevents the slideshow from capturing drag events when interacting with 3D models
        if (event.target.closest('model-viewer')) {
          return;
        }

        // preventDefault sadece drag başladığında (onPointerMove içinde) uygulanır – tıklamaları engellemez
        // Store initial position but don't start handling yet
        const {
          axis
        } = _classPrivateFieldLooseBase(this, _scroll)[_scroll];
        const startPosition = event[axis];
        const controller = new AbortController();
        const {
          signal
        } = controller;
        const startTime = performance.now();
        let previous = startPosition;
        let velocity = 0;
        let moved = false;
        let distanceTravelled = 0;
        _classPrivateFieldLooseBase(this, _dragging)[_dragging] = true;

        /**
         * Handles the 'pointermove' event to update the scroll position.
         * @param {PointerEvent} event - The pointermove event.
         */
        const onPointerMove = event => {
          const current = event[axis];
          const initialDelta = startPosition - current;
          if (!initialDelta) return;
          if (!moved) {
            moved = true;
            this.setPointerCapture(event.pointerId);

            // Prevent clicks once the user starts dragging
            document.addEventListener('click', preventDefault, {
              once: true,
              signal
            });
            const movingRight = initialDelta < 0;
            const movingLeft = initialDelta > 0;

            // Check if the current slideshow should handle this drag
            const closestSlideshow = this.parentElement?.closest('slideshow-component');
            const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
            const cannotMoveInDirection = movingRight && this.atStart || movingLeft && this.atEnd;

            // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
            if (isNested && cannotMoveInDirection) {
              controller.abort();
              return;
            }
            this.pause();
            this.setAttribute('dragging', '');
          }

          // Stop the event from bubbling up to parent slideshow components
          event.stopImmediatePropagation();
          const delta = previous - current;
          const timeDelta = performance.now() - startTime;
          velocity = Math.round(delta / timeDelta * 1000);
          previous = current;
          distanceTravelled += Math.abs(delta);
          _classPrivateFieldLooseBase(this, _scroll)[_scroll].by(delta, {
            instant: true
          });
        };

        /**
         * Handles the 'pointerup' event to stop dragging slides.
         * @param {PointerEvent} event - The pointerup event.
         */
        const onPointerUp = async event => {
          controller.abort();
          const {
            current,
            slides
          } = this;
          const {
            scroller
          } = this.refs;
          _classPrivateFieldLooseBase(this, _dragging)[_dragging] = false;
          if (!slides?.length || !scroller) return;
          const direction = Math.sign(velocity);
          const next = _classPrivateFieldLooseBase(this, _sync)[_sync]();
          const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
          const newIndex = clamp(next + modifier, 0, slides.length - 1);
          const newSlide = slides[newIndex];
          const currentIndex = this.current;
          if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);
          _classPrivateFieldLooseBase(this, _scroll)[_scroll].to(newSlide);
          this.removeAttribute('dragging');
          this.releasePointerCapture(event.pointerId);
          _classPrivateFieldLooseBase(this, _centerSelectedThumbnail)[_centerSelectedThumbnail](newIndex);
          this.dispatchEvent(new SlideshowSelectEvent({
            index: newIndex,
            previousIndex: currentIndex,
            userInitiated: true,
            trigger: 'drag',
            slide: newSlide,
            id: newSlide.getAttribute('slide-id')
          }));
          this.current = newIndex;
          await _classPrivateFieldLooseBase(this, _scroll)[_scroll].finished;

          // It's possible that the user started dragging again before the scroll finished
          if (_classPrivateFieldLooseBase(this, _dragging)[_dragging]) return;
          _classPrivateFieldLooseBase(this, _scroll)[_scroll].snap = true;
          this.resume();
        };
        _classPrivateFieldLooseBase(this, _scroll)[_scroll].snap = false;
        document.addEventListener('pointermove', onPointerMove, {
          signal
        });
        document.addEventListener('pointerup', onPointerUp, {
          signal
        });
        /**
         * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
         * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
         * sometimes causing the slideshow to change slides unexpectedly
         */
        document.addEventListener('pointerdown', onPointerUp, {
          signal
        });
        document.addEventListener('pointercancel', onPointerUp, {
          signal
        });
        document.addEventListener('pointercapturelost', onPointerUp, {
          signal
        });
      }
    });
    Object.defineProperty(this, _handlePointerEnter, {
      writable: true,
      value: () => {
        this.setAttribute('actioned', '');
      }
    });
    /**
     * Pause the slideshow when the page is hidden.
     */
    Object.defineProperty(this, _handleVisibilityChange, {
      writable: true,
      value: () => document.hidden ? this.pause() : this.resume()
    });
  }
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !_classPrivateFieldLooseBase(this, _scroll)[_scroll] || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({
            id: slide_id
          }, undefined, {
            animate: false
          });
        }
      });
    }
  }
  async connectedCallback() {
    super.connectedCallback();

    // Register with shared viewport observer for lazy scroll enablement.
    // This prevents iOS Safari crashes caused by too many compositor layers.
    SlideshowViewportObserver.getInstance().observe(this);

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }
    const slideCount = this.slides?.length || 0;
    slideCount <= 1 ? _classPrivateFieldLooseBase(this, _setupSlideshowWithoutControls)[_setupSlideshowWithoutControls]() : _classPrivateFieldLooseBase(this, _setupSlideshow)[_setupSlideshow]();
  }
  disconnectedCallback() {
    super.disconnectedCallback();

    // Unregister from shared viewport observer
    SlideshowViewportObserver.getInstance().unobserve(this);
    if (_classPrivateFieldLooseBase(this, _scroll)[_scroll]) {
      const {
        scroller
      } = this.refs;
      scroller.removeEventListener('mousedown', _classPrivateFieldLooseBase(this, _handleMouseDown)[_handleMouseDown]);
      _classPrivateFieldLooseBase(this, _scroll)[_scroll].destroy();
    }
    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerenter', _classPrivateFieldLooseBase(this, _handlePointerEnter)[_handlePointerEnter]);
      document.removeEventListener('visibilitychange', _classPrivateFieldLooseBase(this, _handleVisibilityChange)[_handleVisibilityChange]);
    }
    if (_classPrivateFieldLooseBase(this, _resizeObserver)[_resizeObserver]) {
      _classPrivateFieldLooseBase(this, _resizeObserver)[_resizeObserver].disconnect();
    }
    if (_classPrivateFieldLooseBase(this, _intersectionObserver)[_intersectionObserver]) {
      _classPrivateFieldLooseBase(this, _intersectionObserver)[_intersectionObserver].disconnect();
      _classPrivateFieldLooseBase(this, _intersectionObserver)[_intersectionObserver] = null;
    }
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }
  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (_classPrivateFieldLooseBase(this, _disabled)[_disabled] || !this.refs.slides?.length) return;
    if (!_classPrivateFieldLooseBase(this, _scroll)[_scroll]) return;

    // Store the actual current slide before any mutations
    const currentSlide = this.slides?.[this.current];
    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find(slide => slide.getAttribute('slide-id') == input.id);
        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }
        return this.slides.indexOf(requestedSlide);
      }
    })();
    const {
      current
    } = this;
    const {
      slides
    } = this;

    // Guard checks: no slides, invalid index, or selecting the same slide
    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;
    const requestedSlideElement = slides?.[requestedIndex];
    if (currentSlide === requestedSlideElement) return;
    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);
    event?.preventDefault();
    const {
      animate = true
    } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;else if (requestedIndex > lastIndex) index = 0;
    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const {
      visibleSlides
    } = this;
    const instant = prefersReducedMotion() || !animate;

    // If jump is more than 1 or we looped, do the placeholder + reorder trick
    if (!instant && !isAdjacentSlide && visibleSlides.length === 1) {
      _classPrivateFieldLooseBase(this, _disabled)[_disabled] = true;
      await _classPrivateFieldLooseBase(this, _scroll)[_scroll].finished; // ensure we're not mid-scroll

      const targetSlide = slides[index];
      if (!targetSlide || !currentSlide) return;

      // Create a placeholder in the original DOM position of targetSlide
      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      // Decide whether targetSlide goes before or after currentSlide
      // so that we scroll a short distance in the correct direction
      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }
      if (current === 0) _classPrivateFieldLooseBase(this, _scroll)[_scroll].to(currentSlide, {
        instant: true
      });

      // Once that scroll finishes, restore the DOM
      queueMicrotask(async () => {
        await _classPrivateFieldLooseBase(this, _scroll)[_scroll].finished;
        _classPrivateFieldLooseBase(this, _disabled)[_disabled] = false;

        // Restore the slide back to its original position. This triggers a scroll event.
        placeholder.replaceWith(targetSlide);

        // Instantly scroll to the target slide as its position will have changed
        _classPrivateFieldLooseBase(this, _scroll)[_scroll].to(targetSlide, {
          instant: true
        });

        // Force Safari to recalculate the timeline state on timeline refresh (after loop)
        requestAnimationFrame(() => {
          this.setAttribute('refreshing-timeline', '');
          requestAnimationFrame(() => {
            this.removeAttribute('refreshing-timeline');
          });
        });
      });
    }
    const slide = slides[index];
    if (!slide) return;
    const previousIndex = this.current;
    slide.setAttribute('aria-hidden', 'false');
    if (_classPrivateFieldLooseBase(this, _scroll)[_scroll]) {
      _classPrivateFieldLooseBase(this, _scroll)[_scroll].to(slide, {
        instant
      });
    }
    this.current = this.slides?.indexOf(slide) || 0;
    _classPrivateFieldLooseBase(this, _centerSelectedThumbnail)[_centerSelectedThumbnail](index, instant ? 'instant' : 'smooth');
    this.dispatchEvent(new SlideshowSelectEvent({
      index,
      previousIndex,
      userInitiated: event != null,
      trigger: 'select',
      slide,
      id: slide.getAttribute('slide-id')
    }));
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (_classPrivateFieldLooseBase(this, _interval)[_interval]) return;
    this.paused = false;
    _classPrivateFieldLooseBase(this, _interval)[_interval] = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }
  get paused() {
    return this.hasAttribute('paused');
  }
  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(_classPrivateFieldLooseBase(this, _interval)[_interval]);
    _classPrivateFieldLooseBase(this, _interval)[_interval] = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;
    this.pause();
    this.play();
  }
  get autoplay() {
    return Boolean(this.autoplayInterval);
  }
  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);
    if (Number.isNaN(value)) return undefined;
    return value * 1000;
  }
  get current() {
    return _classPrivateFieldLooseBase(this, _current)[_current];
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const {
      current,
      thumbnails,
      dots,
      slides,
      previous,
      next
    } = this.refs;
    _classPrivateFieldLooseBase(this, _current)[_current] = value;
    if (current) current.textContent = `${value + 1}`;
    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));
    }
    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }
  get infinite() {
    return this.getAttribute('infinite') != null;
  }
  get visibleSlides() {
    return _classPrivateFieldLooseBase(this, _visibleSlides)[_visibleSlides];
  }
  get slideBy() {
    const attr = Number(this.getAttribute('slide-by'));
    return Number.isFinite(attr) && attr > 0 ? attr : null;
  }
  get previousIndex() {
    const {
      current,
      visibleSlides
    } = this;
    const modifier = this.slideBy ?? (visibleSlides.length > 1 ? visibleSlides.length : 1);
    return current - modifier;
  }
  get nextIndex() {
    const {
      current,
      visibleSlides
    } = this;
    const modifier = this.slideBy ?? (visibleSlides.length > 1 ? visibleSlides.length : 1);
    return current + modifier;
  }
  get atStart() {
    const {
      current,
      slides
    } = this;
    return slides?.length ? current === 0 : false;
  }
  get atEnd() {
    const {
      current,
      slides
    } = this;
    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return this.getAttribute('disabled') === 'true' || this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches;
  }
  get slides() {
    return this.refs.slides?.filter(slide => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;
    return parseInt(initialSlide, 10);
  }
}
function _setupSlideshowWithoutControls2() {
  this.current = 0;
  if (this.hasAttribute('auto-hide-controls')) {
    const {
      slideshowControls
    } = this.refs;
    if (slideshowControls instanceof HTMLElement) {
      slideshowControls.hidden = true;
    }
  }
  if (this.refs.slides?.[0]) {
    this.refs.slides[0].setAttribute('aria-hidden', 'false');
  }
}
function _setupSlideshow2() {
  // Setup IntersectionObserver first for efficient visibility tracking
  _classPrivateFieldLooseBase(this, _setupIntersectionObserver)[_setupIntersectionObserver]();

  // Setup the scroll instance
  const {
    scroller
  } = this.refs;
  _classPrivateFieldLooseBase(this, _scroll)[_scroll] = new Scroller(scroller, {
    onScroll: _classPrivateFieldLooseBase(this, _handleScroll)[_handleScroll],
    onScrollStart: _classPrivateFieldLooseBase(this, _onTransitionInit)[_onTransitionInit],
    onScrollEnd: _classPrivateFieldLooseBase(this, _onTransitionEnd)[_onTransitionEnd]
  });

  /**
   * data-video-carousel: autoslide + video card-gallery’de card-gallery-video-hover.js.
   * Tema drag + suspend/resume çakışmasın.
   */
  if (!this.hasAttribute('data-video-carousel')) {
    scroller.addEventListener('mousedown', _classPrivateFieldLooseBase(this, _handleMouseDown)[_handleMouseDown]);
    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
  }
  this.addEventListener('pointerenter', _classPrivateFieldLooseBase(this, _handlePointerEnter)[_handlePointerEnter]);
  document.addEventListener('visibilitychange', _classPrivateFieldLooseBase(this, _handleVisibilityChange)[_handleVisibilityChange]);
  _classPrivateFieldLooseBase(this, _updateControlsVisibility)[_updateControlsVisibility]();
  this.disabled = this.isNested || this.disabled;
  this.resume();
  this.current = this.initialSlideIndex;

  // Batch reads and writes to the DOM
  scheduler.schedule(() => {
    let visibleSlidesAmount = 0;
    const initialSlideId = this.initialSlide?.getAttribute('slide-id');

    // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
    // This prevents race conditions on Safari mobile when section_width is 'full-width'
    requestAnimationFrame(() => {
      if (this.initialSlideIndex !== 0 && initialSlideId) {
        this.select({
          id: initialSlideId
        }, undefined, {
          animate: false
        });
        visibleSlidesAmount = 1;
      } else {
        visibleSlidesAmount = _classPrivateFieldLooseBase(this, _updateVisibleSlides)[_updateVisibleSlides]();
        if (visibleSlidesAmount === 0) {
          this.select(0, undefined, {
            animate: false
          });
          visibleSlidesAmount = 1;
        }
      }
    });
    _classPrivateFieldLooseBase(this, _resizeObserver)[_resizeObserver] = new ResizeObserver(async () => {
      if (viewTransition.current) await viewTransition.current;
      if (visibleSlidesAmount > 1) {
        _classPrivateFieldLooseBase(this, _updateVisibleSlides)[_updateVisibleSlides]();
      }
      if (this.hasAttribute('auto-hide-controls')) {
        _classPrivateFieldLooseBase(this, _updateControlsVisibility)[_updateControlsVisibility]();
      }
    });
    _classPrivateFieldLooseBase(this, _resizeObserver)[_resizeObserver].observe(this.refs.slideshowContainer);
  });
}
function _updateControlsVisibility2() {
  if (!this.hasAttribute('auto-hide-controls')) return;
  const {
    scroller,
    slideshowControls
  } = this.refs;
  if (!(slideshowControls instanceof HTMLElement)) return;
  slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
}
function _setupIntersectionObserver2() {
  const {
    slides,
    scroller
  } = this.refs;
  if (!slides?.length) return;
  if (_classPrivateFieldLooseBase(this, _intersectionObserver)[_intersectionObserver]) {
    _classPrivateFieldLooseBase(this, _intersectionObserver)[_intersectionObserver].disconnect();
  }
  _classPrivateFieldLooseBase(this, _intersectionObserver)[_intersectionObserver] = new IntersectionObserver(entries => {
    const allEntries = [...entries, ...(_classPrivateFieldLooseBase(this, _intersectionObserver)[_intersectionObserver] ? _classPrivateFieldLooseBase(this, _intersectionObserver)[_intersectionObserver].takeRecords() : [])];
    for (const entry of allEntries) {
      const slide = /** @type {HTMLElement} */entry.target;
      const isCurrentlyVisible = _classPrivateFieldLooseBase(this, _visibleSlides)[_visibleSlides].includes(slide);
      const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;
      if (shouldBeVisible && !isCurrentlyVisible) {
        _classPrivateFieldLooseBase(this, _visibleSlides)[_visibleSlides].push(slide);
      } else if (!shouldBeVisible && isCurrentlyVisible) {
        const index = _classPrivateFieldLooseBase(this, _visibleSlides)[_visibleSlides].indexOf(slide);
        if (index > -1) {
          _classPrivateFieldLooseBase(this, _visibleSlides)[_visibleSlides].splice(index, 1);
        }
      }
    }
    _classPrivateFieldLooseBase(this, _visibleSlides)[_visibleSlides].sort((a, b) => slides.indexOf(a) - slides.indexOf(b));
    _classPrivateFieldLooseBase(this, _updateVisibleSlides)[_updateVisibleSlides]();
  }, {
    root: scroller,
    threshold: SLIDE_VISIBLITY_THRESHOLD,
    // Add small margin to account for sub-pixel rendering
    rootMargin: '1px'
  });

  // Observe all slides - observer will fire initial callback asynchronously
  slides.forEach(slide => {
    _classPrivateFieldLooseBase(this, _intersectionObserver)[_intersectionObserver]?.observe(slide);
  });
}
function _centerSelectedThumbnail2(index, behavior = 'smooth') {
  const selectedThumbnail = this.refs.thumbnails?.[index];
  if (!selectedThumbnail) return;
  const {
    thumbnailsContainer
  } = this.refs;
  if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;
  const {
    slideshowControls
  } = this.refs;
  if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;
  scrollIntoView(selectedThumbnail, {
    ancestor: thumbnailsContainer,
    behavior,
    block: 'center',
    inline: 'center'
  });
}
function _updateVisibleSlides2() {
  const {
    slides
  } = this;
  if (!slides || !slides.length) return 0;
  const visibleSlides = this.visibleSlides;

  // Batch writes to the DOM
  scheduler.schedule(() => {
    // Update aria-hidden based on visibility
    slides.forEach(slide => {
      const isVisible = visibleSlides.includes(slide);
      slide.setAttribute('aria-hidden', `${!isVisible}`);
    });
  });
  return visibleSlides.length;
}
if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}

/**
 * Arrow click: product-card link overlay'in arrow tıklamalarını
 * yakalayıp sayfayı navigate etmesini engelle.
 * stopPropagation YAPMA — temanın kendi slideshow navigation'ı çalışmalı.
 */
document.addEventListener('click', e => {
  const btn = e.target.closest('.slideshow-control[data-action]');
  if (!btn) return;
  e.preventDefault();
}, {
  capture: true
});
